import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Square, Camera, MapPin, Clock, AlertCircle, CheckCircle, Upload, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export const RecordIncident = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [incidentId, setIncidentId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, recording, processing, complete
  
  const [formData, setFormData] = useState({
    title: '',
    incident_type: 'accident',
    severity: 'minor',
    description: '',
    location: null
  });

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    // Get GPS location on mount
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy
            }
          }));
          toast.success('Location captured');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Could not get location');
        }
      );
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      // Create the incident FIRST so we have a stable ID before any
      // mediaRecorder events fire. Pass `newIncidentId` explicitly through
      // the handler chain to avoid React state-closure bugs (otherwise
      // mediaRecorder.onstop closes over `incidentId === null` and the
      // upload silently no-ops, leaving the UI stuck on "processing").
      const newIncidentId = await createIncident();
      if (!newIncidentId) return;
      setIncidentId(newIncidentId);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await uploadAudio(audioBlob, newIncidentId);
        } catch (err) {
          console.error('onstop pipeline error:', err);
          toast.error('Failed to process recording');
          setStatus('idle');
        } finally {
          stream.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      setRecording(true);
      setStatus('recording');

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording. Please allow microphone access.');
      setStatus('idle');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setStatus('processing');
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      toast.info('Processing recording...');
    }
  };

  const createIncident = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const formPayload = new FormData();
      formPayload.append('title', formData.title || 'Emergency Incident');
      formPayload.append('incident_type', formData.incident_type);
      formPayload.append('severity', formData.severity);
      if (formData.description) formPayload.append('description', formData.description);
      
      if (formData.location) {
        formPayload.append('location_lat', formData.location.lat);
        formPayload.append('location_lng', formData.location.lng);
      }

      const response = await fetch(`${backendUrl}/api/incidents`, {
        method: 'POST',
        credentials: 'include',
        body: formPayload
      });

      if (!response.ok) throw new Error('Failed to create incident');

      const data = await response.json();
      return data.incident_id;
    } catch (error) {
      console.error('Create incident error:', error);
      toast.error('Failed to create incident');
      return null;
    }
  };

  const uploadAudio = async (audioBlob, idArg) => {
    const id = idArg || incidentId;
    if (!id) {
      console.error('uploadAudio called without an incident ID');
      toast.error('Could not save recording (missing incident ID)');
      setStatus('idle');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const formData = new FormData();
      formData.append('file', audioBlob, 'incident-audio.webm');

      const response = await fetch(`${backendUrl}/api/incidents/${id}/audio`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to upload audio');

      toast.success('Audio saved successfully');

      // Start transcription (pass id explicitly to avoid stale closure)
      await transcribeAudio(id);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload audio');
      // Route to the new IncidentDetail page so the user can immediately
      // see their recording with audio playback + transcript + photos.
      setStatus('complete');
      setTimeout(() => navigate(`/app/incidents/${id}`), 1500);
    }
  };

  const transcribeAudio = async (idArg) => {
    const id = idArg || incidentId;
    if (!id) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/incidents/${id}/transcribe`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Transcription failed');

      await response.json();
      toast.success('Transcription complete');
      setStatus('complete');

      // Route to the IncidentDetail page with audio player + transcript.
      setTimeout(() => {
        navigate(`/app/incidents/${id}`);
      }, 1500);
    } catch (error) {
      console.error('Transcription error:', error);
      toast.warning('Audio saved but transcription failed');
      setStatus('complete');
      setTimeout(() => navigate(`/app/incidents/${id}`), 1500);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 mb-4">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Record Incident</h1>
          <p className="text-muted-foreground">Capture audio evidence and details</p>
        </motion.div>

        {/* Status Badge */}
        {status !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex justify-center"
          >
            <Badge 
              variant={status === 'complete' ? 'default' : 'secondary'}
              className={`text-lg px-6 py-2 ${
                status === 'recording' ? 'bg-red-500 text-white animate-pulse' :
                status === 'processing' ? 'bg-yellow-500 text-white' :
                'bg-green-500 text-white'
              }`}
            >
              {status === 'recording' && '🔴 Recording...'}
              {status === 'processing' && '⏳ Processing...'}
              {status === 'complete' && '✅ Complete'}
            </Badge>
          </motion.div>
        )}

        {/* Recording Timer */}
        {recording && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-card border-2 border-red-500">
              <Clock className="w-6 h-6 text-red-500" />
              <span className="text-4xl font-mono font-bold">{formatTime(recordingTime)}</span>
            </div>
          </motion.div>
        )}

        {/* Incident Details Form */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Incident Details</CardTitle>
            <CardDescription>Provide information about what happened</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Incident Title</Label>
              <Input
                id="title"
                placeholder="Brief description (e.g., 'Rear-end collision on M25')"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={recording}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.incident_type}
                  onValueChange={(value) => setFormData({ ...formData, incident_type: value })}
                  disabled={recording}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accident">Car Accident</SelectItem>
                    <SelectItem value="breakdown">Breakdown</SelectItem>
                    <SelectItem value="theft_attempt">Theft Attempt</SelectItem>
                    <SelectItem value="dispute">Dispute</SelectItem>
                    <SelectItem value="traffic_stop">Traffic Stop</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="severity">Severity</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(value) => setFormData({ ...formData, severity: value })}
                  disabled={recording}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="severe">Severe</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Location Status */}
            {formData.location && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <MapPin className="w-4 h-4" />
                <span>Location captured ({formData.location.accuracy.toFixed(0)}m accuracy)</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recording Controls */}
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              {!recording && status === 'idle' && (
                <Button
                  onClick={startRecording}
                  size="lg"
                  className="w-full h-16 text-lg gap-2 bg-red-500 hover:bg-red-600"
                  disabled={!formData.title}
                >
                  <Mic className="w-6 h-6" />
                  Start Recording
                </Button>
              )}

              {recording && (
                <Button
                  onClick={stopRecording}
                  size="lg"
                  className="w-full h-16 text-lg gap-2 bg-gray-800 hover:bg-gray-900"
                >
                  <Square className="w-6 h-6" />
                  Stop Recording
                </Button>
              )}

              {status === 'complete' && (
                <div className="text-center space-y-2">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                  <p className="font-medium">Incident recorded successfully!</p>
                  <p className="text-sm text-muted-foreground">Redirecting to details...</p>
                </div>
              )}

              {!recording && status === 'idle' && !formData.title && (
                <p className="text-sm text-muted-foreground text-center">
                  Please enter an incident title to begin recording
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        {status === 'idle' && (
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Recording Tips
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Speak clearly and describe what you see</li>
                <li>• Include license plates, vehicle colors, and damage</li>
                <li>• Note any injuries and if emergency services were called</li>
                <li>• Record witness information if available</li>
                <li>• Audio will be transcribed automatically</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RecordIncident;
